import { G } from './ctx';
import { Vehicle } from './vehicle';
import { Ped } from './ped';
import type { HumanoidStyle, IEntityManager, IPed, IVehicle, PedRole, VehicleType } from './types';

/** Registry of live vehicles and peds. Spawning always goes through here. */
export class EntityManager implements IEntityManager {
  vehicles: IVehicle[] = [];
  peds: IPed[] = [];

  spawnVehicle(type: VehicleType, x: number, z: number, heading: number, opts: { parked?: boolean; persistent?: boolean; color?: number } = {}): IVehicle {
    const v = new Vehicle(type, x, z, heading, opts.color);
    v.parked = !!opts.parked;
    v.persistent = !!opts.persistent;
    this.vehicles.push(v);
    G.scene.add(v.group);
    return v;
  }

  spawnPed(role: PedRole, x: number, z: number, opts: { style?: Partial<HumanoidStyle>; persistent?: boolean; controlled?: boolean } = {}): IPed {
    const p = new Ped(role, x, z, opts.style);
    p.persistent = !!opts.persistent;
    p.controlled = !!opts.controlled;
    if (p.controlled) p.state = 'controlled';
    this.peds.push(p);
    G.scene.add(p.group);
    return p;
  }

  removeVehicle(v: IVehicle): void {
    const i = this.vehicles.indexOf(v);
    if (i >= 0) this.vehicles.splice(i, 1);
    if (v.driver && v.driver !== 'player') v.driver.exitVehicle();
    for (const p of [...v.passengers]) p.exitVehicle();
    G.scene.remove(v.group);
    v.dispose();
  }

  removePed(p: IPed): void {
    const i = this.peds.indexOf(p);
    if (i >= 0) this.peds.splice(i, 1);
    if (p.vehicle) p.exitVehicle();
    G.scene.remove(p.group);
    p.dispose();
  }

  nearestVehicle(x: number, z: number, maxDist: number, filter?: (v: IVehicle) => boolean): IVehicle | null {
    let best: IVehicle | null = null;
    let bd = maxDist * maxDist;
    for (const v of this.vehicles) {
      if (filter && !filter(v)) continue;
      const dx = v.pos.x - x;
      const dz = v.pos.z - z;
      const d = dx * dx + dz * dz;
      if (d < bd) {
        bd = d;
        best = v;
      }
    }
    return best;
  }

  nearestPed(x: number, z: number, maxDist: number, filter?: (p: IPed) => boolean): IPed | null {
    let best: IPed | null = null;
    let bd = maxDist * maxDist;
    for (const p of this.peds) {
      if (filter && !filter(p)) continue;
      const dx = p.pos.x - x;
      const dz = p.pos.z - z;
      const d = dx * dx + dz * dz;
      if (d < bd) {
        bd = d;
        best = p;
      }
    }
    return best;
  }
}
